import React from "react";
import { PortableTextBlock } from "../../sanity/portableTextBlock";
import { BlockWrapper } from "../blockWrapper";
import { ExternalLink } from "../../ui";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useLocation } from "@reach/router";
import { motion } from "framer-motion";
import cx from "classnames";

function encode(data) {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

const ContactForm = ({
  blockConfig,
  _rawAddress,
  emailplaceholder: emailPlaceHolder,
  nameplaceholder: namePlaceHolder,
  messageplaceholder: messagePlaceholder,
  numberplaceholder: numberPlaceholder,
  submittext: submitText,
}) => {
  const { pathname } = useLocation();

  const formCommonClassName =
    "py-2 pb-3 border-b border-dark-navy placeholder:text-dark-navy";
  const formCommonStyle = { backgroundColor: "transparent" };

  return (
    <BlockWrapper {...blockConfig} className="grid grid-cols-14">
      <div className="col-start-2 col-end-14 row-start-1 row-end-2 lg:mt-0 text-center lg:text-left mb-6 lg:my-24 lg:px-24">
        <PortableTextBlock
          text={_rawAddress}
          className="prose prose-contact flex-col lg:flex-row flex justify-around items-center mx-auto"
        />
      </div>

      {/* Hidden until further notice */}
      {/* <>
      <Formik
        initialValues={{
          email: "",
        }}
        validate={(values) => {
          const errors = {};

          if (!values.email) {
            errors.email = "Email address is Required";
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = "Invalid email address";
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting, setStatus }) => {
          fetch(pathname || "/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: encode({
              "form-name": `contact-form`,
              ...values,
            }),
          })
            .then(() => {
              setSubmitting(false);
              setStatus("success");
            })
            .catch((error) => alert(error));
        }}
      >
        {({
          onChange,
          isSubmitting,
          status,
          values,
        }) => (
          <div
            className={cx(
              "col-start-2 lg:col-start-7 col-end-14 rounded-xl",
              status === "success"
                ? "flex justify-center items-center bg-none"
                : "mt-12 lg:mt-0 bg-slate/5"
            )}
          >
            <Form
              name="contact-form"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              subject="Website Enquiry"
              className="flex flex-col p-6 lg:p-12 gap-y-6 text-dark-navy"
            >
              <input type="hidden" name="form-name" value="contact-form" />
              <p hidden>
                <label>
                  <input name="bot-field" onChange={onChange} />
                </label>
              </p>
              {status === "success" ? (
                <div className="col-span-full text-center my-10">
                  <motion.h2
                    className="flex font-bold text-teal focus:outline blockH5"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    Thanks for getting in touch with us at Castle Point Funds.
                    We will contact you as soon as possible.
                  </motion.h2>
                </div>
              ) : (
                <>
                  <Field
                    required
                    type="text"
                    name="name"
                    className={formCommonClassName}
                    style={formCommonStyle}
                    placeholder={namePlaceHolder}
                  />
                  <Field
                    required
                    type="number"
                    name="number"
                    className={formCommonClassName}
                    style={formCommonStyle}
                    placeholder={numberPlaceholder}
                  />
                  <Field
                    required
                    type="email"
                    name="email"
                    className={formCommonClassName}
                    style={formCommonStyle}
                    placeholder={emailPlaceHolder}
                  />
                  <Field
                    required
                    type="textarea"
                    name="message"
                    rows="6"
                    as="textarea"
                    className={formCommonClassName}
                    style={formCommonStyle}
                    placeholder={messagePlaceholder}
                  />
                  <button
                    className="flex ml-auto font-bold text-teal focus:outline blockH6 hover:opacity-75"
                    type="submit"
                  >
                    {submitText}
                  </button>
                </>
              )}
            </Form>
          </div>
        )}
      </Formik>
      </> */}

      {/* Complaints & External Dispute Resolution */}
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 col-start-2 col-end-14 mt-12">
      <div>
        <h2 className="blockH4 text-teal">Complaints</h2>
        <p className="my-4">
          Devon Funds Management ('Devon', 'we' or 'us') is committed to
          providing great service and products to our clients. However,
          there may be occasions where we don't meet your expectations. If
          you are not satisfied with a product or service that we have
          provided, we would like to hear from you using our contact
          details below:
        </p>
        <p>The Compliance Manager</p>
        <p>PO Box 105609</p>
        <p>Auckland City 1143</p>
        <p>
          <span className="font-bold">Phone:</span>{" "}
          <a className="text-teal font-bold" href="tel:+6499253990">
            +64 9 925 3990
          </a>{" "}
          or{" "}
          <a className="text-teal font-bold" href="tel:0800944049">
            0800 944 049
          </a>
        </p>
        <p>
          <span className="font-bold">Email:</span>{" "}
          <a
            className="text-teal font-bold"
            href="mailto:enquiries@devonfunds.co.nz"
          >
            enquiries@devonfunds.co.nz
          </a>
        </p>
        <p className="my-4">Below is a summary of our complaints process:</p>
        <ul className="list-item list-disc ml-6 mb-4">
          <li>You send us your complaint along with any supporting evidence</li>
          <li>We acknowledge receipt of your complaint and log it</li>
          <li>
            If we cannot resolve your complaint immediately, we will work
            with you to investigate further and determine next steps
          </li>
          <li>We aim to resolve your complaint as soon as possible</li>
          <li>
            We will notify you in writing of the outcome of the
            investigation and where needed, help you escalate to our
            external Disputes Resolution Scheme (DRS).
          </li>
        </ul>
      </div>
      <div>
        <h2 className="blockH4 text-teal">External Dispute Resolution</h2>
        <p className="my-4">
          Devon Funds Management is a member of an independent Dispute
          Resolution Service (DRS), the Insurance &amp; Financial Services
          Ombudsman Scheme, an approved DRS for the purposes of the
          Financial Service Providers (Registration and Dispute
          Resolution) Act 2008.
        </p>
        <p className="my-4">
          If you are not satisfied with the response received, you may
          refer the matter (free of charge), directly to the IFSO by:
        </p>
        <ul className="list-item list-disc ml-6 mb-4">
          <li>
            visiting{" "}
            <ExternalLink
              className="text-teal font-bold"
              link="https://www.ifso.nz"
              linkText="www.ifso.nz"
            />
          </li>
          <li>
            calling{" "}
            <a className="text-teal font-bold" href="tel:0800888202">
              0800 888 202
            </a>
          </li>
          <li>
            emailing{" "}
            <a className="text-teal font-bold" href="mailto:info@ifso.nz">
              info@ifso.nz
            </a>
          </li>
        </ul>
      </div>
    </div>
    </BlockWrapper>
  );
};

export default ContactForm;
